import { useCallback, useMemo } from 'react';
import { GetStaticPathsResult, GetStaticPropsContext } from 'next';
import Image from 'next/image';
import YouTube from 'react-youtube';

import { Box, Flex, Heading, useToast } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { Alert } from '~/components/alert';
import { ButtonLink } from '~/components/button-link';
import { FAQSection } from '~/components/faq-section';
import { LinesBannerSection } from '~/components/lines-banner-section';
import { Markdown } from '~/components/markdown';
import { ModalButton } from '~/components/modal-button';
import { NotifyMeForm } from '~/components/notify-me-form';
import { SanityImage } from '~/components/sanity-image';
import { ShapesBanner } from '~/components/shapes-banner';
import { StandardHeroSection } from '~/components/standard-hero';
import { TestimonialSection } from '~/components/testimonial-section';
import { YouTubeVideoSection } from '~/components/youtube-video-section';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { AnalyticsEvent } from '~/features/analytics/types';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { AudienceSection } from '~/features/scholarships/components/audience-section';
import { CompanyAboutSection } from '~/features/scholarships/components/company-about-section';
import { HowDoesItWorkSection } from '~/features/scholarships/components/how-does-it-work-section';
import { ProgramOfferedSection } from '~/features/scholarships/components/programs-offered-section';
import { ScholarshipFeaturesSection } from '~/features/scholarships/components/scholarship-features-section';
import { ScholarshipPageBodySection } from '~/features/scholarships/components/scholarship-page-body-section';
import { TimelineSection } from '~/features/scholarships/components/timeline-section';
import { getAllScholarshipContent } from '~/features/scholarships/controllers/get-all-scholarship-content';
import { getScholarshipContent } from '~/features/scholarships/controllers/get-scholarship-content';
import { ScholarshipContent } from '~/features/scholarships/models/scholarship-content';
import { getScholarshipProgramsOffered } from '~/features/scholarships/utils/get-scholarship-programs-offered';
import { getScholarshipState } from '~/features/scholarships/utils/get-scholarship-state';

function ExpiredScholarshipAlert({ message }: { message?: string | null }) {
  const fallbackMessage =
    'Please note: Applications for this program have closed, but you can sign up below to be notified for future programs.' as const;
  return (
    <Box
      position='relative'
      marginBottom={{
        base: '40px',
        md: 'unset',
      }}
      top={{
        md: '-56px',
      }}
    >
      <Alert color='black' status='info'>
        <Markdown markdownText={message ?? fallbackMessage} />
      </Alert>
    </Box>
  );
}

export default function ScholarshipPage({
  page,
  scholarshipContent,
  programsOffered,
}: {
  page: Page;
  scholarshipContent: ScholarshipContent;
  programsOffered?: Array<{ key: string; slug: string; title: string; summary: string | null }>;
}) {
  const toast = useToast();
  const scholarshipStatus = useMemo(() => {
    const startDateUtc = new Date(scholarshipContent.applicationStartDate);
    const endDateUtc = new Date(scholarshipContent.applicationEndDate);
    return getScholarshipState(startDateUtc, endDateUtc);
  }, [scholarshipContent.applicationStartDate, scholarshipContent.applicationEndDate]);

  const PrimaryButton = useCallback(
    ({ analyticsEvent, colorScheme = 'blue' }: { analyticsEvent?: Partial<AnalyticsEvent>; colorScheme?: string }) => {
      const buttonProps = {
        size: 'icon-right',
        variant: 'solid',
        colorScheme,
        rightIcon: <ArrowRightIcon height='24px' width='24px' />,
        width: {
          base: '100%',
          md: 'fit-content',
        },
      };

      if (
        (scholarshipStatus === 'expired' || scholarshipStatus === 'inactive') &&
        scholarshipContent.leadGenerationForm
      )
        return (
          <ModalButton
            buttonProps={{
              ...buttonProps,
              onClick: () =>
                trackEvent({
                  name: 'Scholarship Page CTA Clicked',
                  type: 'click',
                  label: 'notify_me_clicked',
                }),
            }}
            modalProps={{
              title: 'Get notified for future scholarship opportunities.',
              size: '3xl',
              renderModalContent: ({ onClose }) => (
                <NotifyMeForm
                  templateForm={scholarshipContent.leadGenerationForm}
                  onCancelClick={onClose}
                  onError={() =>
                    toast({
                      variant: 'error',
                      title: 'Oops. Something went wrong. Please try again.',
                    })
                  }
                  onSubmit={() =>
                    toast({
                      title: 'Thank you for your interest!',
                      variant: 'success',
                    })
                  }
                />
              ),
            }}
          >
            Notify Me of Future Programs
          </ModalButton>
        );
      return (
        <ButtonLink
          buttonProps={{
            ...buttonProps,
            onClick: () =>
              trackEvent({
                name: 'Scholarship Page CTA Clicked',
                type: 'click',
                label: 'get_started_clicked',
                ...analyticsEvent,
              }),
            width: {
              base: '100%',
              md: 'fit-content',
            },
          }}
          linkProps={{
            href: scholarshipContent.linkToApply,
          }}
        >
          Get Started
        </ButtonLink>
      );
    },
    [scholarshipContent, scholarshipStatus, toast]
  );
  const testimonial = scholarshipContent.testimonials?.[0];
  const pageTheme = scholarshipContent.theme ?? 'dark';
  const companyLogoTheme = scholarshipContent.affiliateCompanyLogoVariant ?? 'lofiLogoLight';
  const companyLogo = scholarshipContent.affiliateCompany?.[companyLogoTheme];
  const heroVideoTrailer = scholarshipContent?.heroTrailerVideo;

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Scholarship} scholarshipContent={scholarshipContent} />
      <PageLayout withoutVerticalPadding>
        <StandardHeroSection
          backgroundTheme={pageTheme}
          alertSlot={
            (scholarshipStatus === 'expired' || scholarshipStatus === 'inactive') && (
              <ExpiredScholarshipAlert message={scholarshipContent.expiredMessage} />
            )
          }
          backgroundImageSlot={
            <Box
              bottom='0px'
              pointerEvents='none'
              position='absolute'
              right='calc((100vw - 70rem)/2)'
              top='0px'
              transform='translateX(302px)'
              display={{
                base: 'none',
                md: 'block',
              }}
            >
              <Box height='654px' position='relative' width='941px'>
                <Image
                  alt=''
                  sizes='941px'
                  src='/images/backgrounds/blue-lines-and-shapes.svg'
                  style={{
                    objectPosition: 'left top',
                    objectFit: 'cover',
                  }}
                  fill
                  priority
                />
              </Box>
            </Box>
          }
          contentSlot={
            <>
              {scholarshipContent.title && (
                <Heading as='h1' size='marketing-5xl'>
                  {scholarshipContent.title}
                </Heading>
              )}
              {companyLogo && (
                <Box
                  order='-1'
                  marginBottom={{
                    base: '8px',
                    md: '16px',
                  }}
                >
                  <SanityImage
                    alt={scholarshipContent.affiliateCompany.name}
                    image={companyLogo}
                    style={{
                      objectFit: 'contain',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
              )}
              {scholarshipContent.description && <Markdown markdownText={scholarshipContent.description} />}
              <Box
                marginTop='20px'
                marginBottom={{
                  base: '24px',
                  md: 'unset',
                }}
              >
                <PrimaryButton
                  colorScheme={pageTheme === 'dark' ? 'sea-foam' : 'blue'}
                  analyticsEvent={{
                    value: 'CTA 1',
                  }}
                />
              </Box>
            </>
          }
          contentSlotContainerProps={{
            flex: '1 1 448px',
          }}
          imageSlot={
            <Flex
              alignItems='center'
              pointerEvents={heroVideoTrailer ? 'auto' : 'none'}
              position='relative'
              flex={{
                md: '1 1 648px',
              }}
              justifyContent={{
                base: 'center',
                md: 'flex-end',
              }}
            >
              <Box
                bg='black'
                borderRadius='28px 8px 8px 8px'
                boxShadow='lg'
                overflow='hidden'
                position='relative'
                width='100%'
                height={{
                  base: '257px',
                  md: '407px',
                }}
                maxWidth={{
                  base: '100%',
                  md: '544px',
                }}
                sx={
                  heroVideoTrailer
                    ? {
                        '& > div, & > div > iframe': {
                          width: '100%',
                          height: '100%',
                        },
                      }
                    : undefined
                }
              >
                {heroVideoTrailer ? (
                  <YouTube
                    title={heroVideoTrailer.title}
                    videoId={heroVideoTrailer.youTubeVideoId}
                    opts={{
                      host: 'https://www.youtube-nocookie.com',
                      playerVars: {
                        rel: 0,
                      },
                    }}
                    onPlay={() =>
                      trackEvent({
                        name: 'Scholarship Page CTA Clicked',
                        type: 'click',
                        label: 'play_video_clicked',
                        value: heroVideoTrailer?.title ?? '',
                      })
                    }
                  />
                ) : (
                  <SanityImage
                    alt=''
                    image={scholarshipContent.image}
                    sizes='544px'
                    style={{
                      objectFit: 'cover',
                    }}
                    fill
                    priority
                  />
                )}
              </Box>
            </Flex>
          }
        />
        {scholarshipContent.audience && (
          <AudienceSection text={scholarshipContent.audience.text} title={scholarshipContent.audience.title} />
        )}
        {scholarshipContent.features && (
          <ScholarshipFeaturesSection
            features={scholarshipContent.features}
            title="Designed with **students' needs** in mind"
          />
        )}
        <ScholarshipPageBodySection
          text={scholarshipContent.pageBodyText?.text}
          title={scholarshipContent.pageBodyText?.title}
          footerSlot={
            <>
              <HowDoesItWorkSection steps={scholarshipContent.howItWorks} title='How does it work?' />
              {scholarshipContent.video && scholarshipContent.videoSectionTitle && (
                <YouTubeVideoSection
                  title={scholarshipContent.videoSectionTitle}
                  video={scholarshipContent.video}
                  onPlayVideo={() =>
                    trackEvent({
                      name: 'Scholarship Page CTA Clicked',
                      type: 'click',
                      label: 'play_video_clicked',
                      value: scholarshipContent.video?.title ?? '',
                    })
                  }
                />
              )}
            </>
          }
        />
        <LinesBannerSection
          glyphName='rocket-smoke-two'
          linkHref='/experience'
          linkText='Learn More'
          title='Success starts with Udacity’s unique learning experience'
          onButtonClick={() => {
            trackEvent({
              name: 'Scholarship Page CTA Clicked',
              type: 'click',
              label: 'learn_more_clicked',
              value: 'Udacity Experience',
            });
          }}
        />

        {testimonial && (
          <TestimonialSection name={testimonial.name} quote={testimonial.quote} title={testimonial.title} />
        )}

        <ProgramOfferedSection
          forcedProgramsOfferedContent={scholarshipContent.forcedProgramsOfferedContent}
          programs={programsOffered}
          title='Program Details'
        />

        <TimelineSection events={scholarshipContent.timeline} title='Timeline' />
        {scholarshipContent.isCompanyAboutSectionVisible &&
          (scholarshipContent.affiliateCompany || scholarshipContent.companyAboutSectionLogoOverride) && (
            <CompanyAboutSection
              about={scholarshipContent.affiliateCompany.about}
              logo={scholarshipContent.companyAboutSectionLogoOverride || companyLogo}
              name={scholarshipContent.affiliateCompany.name}
              theme={pageTheme}
            />
          )}
        {scholarshipContent.faq && (
          <FAQSection
            faq={scholarshipContent.faq}
            titleSlot={
              <Heading as='h2' marginBottom='24px' size='h2'>
                General & Application FAQs
              </Heading>
            }
          />
        )}

        <ShapesBanner
          title={scholarshipContent.footerTitle}
          buttonSlot={
            <PrimaryButton
              colorScheme='sea-foam'
              analyticsEvent={{
                value: 'CTA 2',
              }}
            />
          }
        />
      </PageLayout>
    </>
  );
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const scholarships = await getAllScholarshipContent();
  return {
    fallback: 'blocking',
    paths: scholarships.map((scholarship) => ({
      params: { slug: scholarship.slug, locale: DEFAULT_LANGUAGE },
    })),
  };
}
export async function getStaticProps({ params, locale }: GetStaticPropsContext<{ slug: string }>) {
  const scholarshipSlug = params?.slug;

  if (!scholarshipSlug || locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, scholarshipContent] = await Promise.all([
    getPage('/scholarships/[slug]'),
    getScholarshipContent(scholarshipSlug),
  ]);

  if (!scholarshipContent) {
    return {
      notFound: true,
    };
  }

  // Note: if stakeholder provides forcedProgramsOfferedContent, we don't want to return the programsOffered values from Sanity.
  // This is implemented here to avoid confusion and reduce JSON size.
  if (scholarshipContent.forcedProgramsOfferedContent) {
    scholarshipContent.programsOffered = [];
  }

  return {
    props: {
      page,
      scholarshipContent,
      programsOffered: await getScholarshipProgramsOffered(scholarshipContent.programsOffered),
    },
  };
}
